// Detects whether a free-text input pasted into the Source combobox is actually
// a DOI, ISBN, or URL. Used to short-circuit the typeahead and open the
// Add-source dialog with the right `source_type` and identifier prefilled.

import type { LibrarySourceType } from "@/lib/library";

export type DetectedSourceIdentifier =
  | { kind: "doi"; value: string; suggestedType: LibrarySourceType }
  | { kind: "isbn"; value: string; suggestedType: LibrarySourceType }
  | { kind: "url"; value: string; suggestedType: LibrarySourceType };

const DOI_PATTERN = /\b(10\.\d{4,9}\/[^\s"<>]+)\b/i;
const URL_PATTERN = /^(https?:\/\/[^\s]+)$/i;

const stripIsbnFormatting = (value: string) => value.replace(/[\s-]/g, "").toUpperCase();

const isValidIsbn10 = (raw: string) => {
  if (!/^\d{9}[\dX]$/.test(raw)) return false;
  let sum = 0;
  for (let i = 0; i < 10; i++) {
    const ch = raw[i];
    const digit = ch === "X" ? 10 : Number.parseInt(ch, 10);
    if (Number.isNaN(digit)) return false;
    sum += digit * (10 - i);
  }
  return sum % 11 === 0;
};

const isValidIsbn13 = (raw: string) => {
  if (!/^\d{13}$/.test(raw)) return false;
  let sum = 0;
  for (let i = 0; i < 13; i++) {
    const digit = Number.parseInt(raw[i], 10);
    sum += i % 2 === 0 ? digit : digit * 3;
  }
  return sum % 10 === 0;
};

export const detectSourceIdentifier = (input: string): DetectedSourceIdentifier | null => {
  const trimmed = input.trim();
  if (!trimmed) return null;

  // DOI: bare or wrapped in a doi.org URL.
  const doiMatch = trimmed.match(DOI_PATTERN);
  if (doiMatch) {
    // Strip trailing punctuation that's commonly pasted with a DOI.
    const value = doiMatch[1].replace(/[).,;]+$/, "");
    return { kind: "doi", value, suggestedType: "journal_article" };
  }

  // ISBN: try to extract digits-and-X and validate 10 or 13.
  const candidateIsbn = stripIsbnFormatting(trimmed);
  if (/^(?:97[89])?\d{9}[\dX]$/.test(candidateIsbn)) {
    if (candidateIsbn.length === 13 && isValidIsbn13(candidateIsbn)) {
      return { kind: "isbn", value: candidateIsbn, suggestedType: "book" };
    }
    if (candidateIsbn.length === 10 && isValidIsbn10(candidateIsbn)) {
      return { kind: "isbn", value: candidateIsbn, suggestedType: "book" };
    }
  }

  // URL: bare http(s) link. (DOI is matched first so doi.org URLs win.)
  if (URL_PATTERN.test(trimmed)) {
    return { kind: "url", value: trimmed, suggestedType: "website" };
  }

  return null;
};
